// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useState } from 'react';

import { Card, Stack, Flex, Collapse, ActionIcon, Text, AccordionChevron } from '@mantine/core';

import { InfoIcon } from 'components/Mantine/InfoIcon';
import { emptyChildren } from 'helpers/emptyChildren';
import { DEFAULT_TRANSITIONS_DURATION } from 'styles/theme/useGenerateMantineTheme';

export type DetailSectionProps = {
  title: ReactNode;
  tooltip?: string;
  rightSection?: ReactNode;
  fullWidth?: boolean;
  collapsable?: boolean;
  large?: boolean;
  children: ReactNode;
  noContentMessage?: string;
  showNoContentMessage?: boolean;
};

export const DetailSectionTitle = ({
  title,
  tooltip,
  large,
}: Pick<DetailSectionProps, 'large' | 'title' | 'tooltip'>) => (
  <Flex align="center" gap="xs" fw={600} fz={large ? 'md' : 'sm'} style={{ userSelect: 'none' }}>
    {title}
    <InfoIcon label={tooltip} />
  </Flex>
);

export const DetailSection = ({
  title,
  tooltip,
  rightSection,
  fullWidth = true,
  large,
  collapsable = large,
  noContentMessage,
  showNoContentMessage,
  children,
}: DetailSectionProps) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <Card withBorder w={fullWidth ? '100%' : undefined} p={0}>
      <Flex justify="space-between" align="center" bg="gray.1" px="md" gap="xl" mih={large ? 45 : 35}>
        <DetailSectionTitle title={title} tooltip={tooltip} large={large} />
        <Flex align="center" gap="sm">
          {rightSection}
          {collapsable && (
            <ActionIcon variant="transparent" onClick={() => setExpanded((e) => !e)} size="sm">
              <AccordionChevron
                color="gray"
                style={{
                  transform: expanded ? `rotate(180deg)` : 'rotate(0deg)',
                  transition: `transform ${DEFAULT_TRANSITIONS_DURATION}ms linear`,
                }}
              />
            </ActionIcon>
          )}
        </Flex>
      </Flex>
      <Collapse expanded={expanded} transitionDuration={DEFAULT_TRANSITIONS_DURATION}>
        <Stack px="md" py="xs" gap={showNoContentMessage ? 0 : 6}>
          {(showNoContentMessage || emptyChildren(children)) && (
            <Text fz="sm" c="dimmed">
              {noContentMessage || 'No content to display.'}
            </Text>
          )}
          {children}
        </Stack>
      </Collapse>
    </Card>
  );
};
